
import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useRouter } from '@/lib/navigation';
import { Search, ArrowRight, Star, X } from 'lucide-react';
import { allArticles, categoryColors, type ArticleWithCategory } from '@/lib/articles';
import { getGuideHref } from '@/lib/guide-routes';
import { useTrackSearch } from '@/hooks/use-track-search';
import { useCatalogPowers } from '@/components/painel/powers/powers-cms-context';
import { filterPowersByQuery, type PowerInfo } from '@/lib/powers-data';
import { highlightSearchText } from '@/lib/search-highlight';
import PowerSearchResultRow from '@/components/help-center/powers/PowerSearchResultRow';
import { RocketScrollArea } from '@/components/help-center/CustomScrollbar';

type SearchItem =
  | { kind: 'article'; article: ArticleWithCategory }
  | { kind: 'power'; power: PowerInfo };

const DROPDOWN_LIMIT = 8;
const ARTICLE_LIMIT = 5;

export default function SearchModal() {
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const router = useRouter();
  const { powers: catalogPowers } = useCatalogPowers();

  const articleMatches = useMemo(() => {
    if (query.trim().length < 2) return [];
    const q = query.trim().toLowerCase();
    return [...allArticles]
      .filter((a) => a.title.toLowerCase().includes(q))
      .sort((a, b) => {
        const ta = a.title.toLowerCase();
        const tb = b.title.toLowerCase();
        const score = (t: string) =>
          t === q ? 1000 : t.startsWith(q) ? 800 : t.includes(q) ? 600 : 0;
        return score(tb) - score(ta) || ta.length - tb.length;
      });
  }, [query]);

  const powerMatches = useMemo(
    () =>
      query.trim().length >= 2 ? filterPowersByQuery(catalogPowers, query) : [],
    [catalogPowers, query]
  );

  const items = useMemo((): SearchItem[] => {
    const q = query.trim().toLowerCase();
    type Ranked = { score: number; item: SearchItem };
    const ranked: Ranked[] = [];

    for (const article of articleMatches.slice(0, ARTICLE_LIMIT)) {
      const t = article.title.toLowerCase();
      const score =
        (t === q ? 1000 : t.startsWith(q) ? 800 : t.includes(q) ? 600 : 100) - 5;
      ranked.push({ score, item: { kind: 'article', article } });
    }

    for (const [i, power] of powerMatches.entries()) {
      if (ranked.length >= DROPDOWN_LIMIT && i > DROPDOWN_LIMIT) break;
      const name = power.name.toLowerCase();
      const slug = power.slug.toLowerCase();
      let score = 0;
      if (name === q || slug === q) score = 1000;
      else if (name.startsWith(q) || slug.startsWith(q)) score = 800;
      else if (name.includes(q) || slug.includes(q)) score = 600;
      else score = 100 - i;
      ranked.push({ score, item: { kind: 'power', power } });
    }

    ranked.sort((a, b) => b.score - a.score);
    return ranked.slice(0, DROPDOWN_LIMIT).map((r) => r.item);
  }, [articleMatches, powerMatches, query]);

  const totalCount = articleMatches.length + powerMatches.length;
  useTrackSearch(query, totalCount, isVisible);

  const open = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setIsMounted(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setIsVisible(true)));
  }, []);

  const close = useCallback(() => {
    setIsVisible(false);
    closeTimerRef.current = setTimeout(() => {
      setIsMounted(false);
      setQuery('');
      setActiveIndex(-1);
    }, 200);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        open();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  useLayoutEffect(() => {
    if (!isVisible) {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.documentElement.style.removeProperty('--app-scroll-lock-gap');
      return;
    }

    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (gap > 0) {
      document.body.style.paddingRight = `${gap}px`;
      document.documentElement.style.setProperty('--app-scroll-lock-gap', `${gap}px`);
    } else {
      document.documentElement.style.setProperty('--app-scroll-lock-gap', '0px');
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.documentElement.style.removeProperty('--app-scroll-lock-gap');
    };
  }, [isVisible]);

  useEffect(() => {
    if (isVisible) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isVisible]);

  const handleSelectArticle = useCallback(
    (article: ArticleWithCategory) => {
      router.push(getGuideHref(article.category, article.slug));
      close();
    },
    [router, close]
  );

  const handleSelectPower = useCallback(
    (power: PowerInfo) => {
      router.push(`/powers/${power.slug}`);
      close();
    },
    [router, close]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      const item = items[activeIndex];
      if (item.kind === 'article') handleSelectArticle(item.article);
      else handleSelectPower(item.power);
    } else if (e.key === 'Escape') {
      close();
    }
  };

  if (!isMounted) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
      <div
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={close}
      />

      <div className={`relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-200 ${isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-3'}`}>
        <div className="flex items-center gap-4 px-5 py-4">
          <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(-1);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Buscar guias e powers..."
            className="flex-1 text-base text-gray-900 placeholder-gray-400 focus:outline-none font-poppins"
          />
          {query ? (
            <button onClick={() => setQuery('')} className="p-1 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          ) : (
            <kbd className="hidden sm:inline-flex items-center px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-md font-medium flex-shrink-0">
              ESC
            </kbd>
          )}
        </div>

        {query.trim().length >= 2 && (
          <div className="border-t border-gray-100">
            {items.length === 0 ? (
              <div className="px-8 py-10 text-center">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Search className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-gray-700 font-semibold">Nenhum resultado encontrado</p>
                <p className="text-sm text-gray-400 mt-1">Tente buscar por outros termos</p>
              </div>
            ) : (
              <>
                <div className="px-5 pt-3 pb-2">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {totalCount} resultado{totalCount !== 1 ? 's' : ''} para &ldquo;{query}&rdquo;
                  </span>
                </div>
                <RocketScrollArea className="max-h-72">
                  <ul>
                  {items.map((item, i) =>
                    item.kind === 'article' ? (
                      <li key={`a-${item.article.category}-${item.article.id}`} className="border-b border-gray-50 last:border-b-0">
                        <button
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleSelectArticle(item.article)}
                          onMouseEnter={() => setActiveIndex(i)}
                          className={`w-full flex items-center gap-4 px-5 py-3.5 transition-colors text-left ${activeIndex === i ? 'bg-blue-50' : 'hover:bg-blue-50'}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1.5">
                              {item.article.popular && (
                                <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 flex-shrink-0" />
                              )}
                              <span className="text-sm font-semibold text-gray-900 line-clamp-1 font-poppins">
                                {highlightSearchText(item.article.title, query)}
                              </span>
                            </div>
                            <span className={`inline-flex text-xs px-2.5 py-1 rounded-full font-semibold ${categoryColors[item.article.category]}`}>
                              {item.article.categoryName}
                            </span>
                          </div>
                          <ArrowRight className={`w-4 h-4 flex-shrink-0 transition-all ${activeIndex === i ? 'text-blue-500 translate-x-0.5' : 'text-gray-300'}`} />
                        </button>
                      </li>
                    ) : (
                      <PowerSearchResultRow
                        key={`p-${item.power.slug}`}
                        power={item.power}
                        query={query}
                        active={activeIndex === i}
                        onSelect={() => handleSelectPower(item.power)}
                        onHover={() => setActiveIndex(i)}
                      />
                    )
                  )}
                  </ul>
                </RocketScrollArea>
              </>
            )}
          </div>
        )}

        <div className="flex items-center gap-5 px-5 py-3 bg-gray-50 border-t border-gray-100">
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <kbd className="inline-flex items-center px-1.5 py-0.5 bg-white border border-gray-200 rounded text-gray-500 text-xs font-medium">↑↓</kbd>
            <span>navegar</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <kbd className="inline-flex items-center px-1.5 py-0.5 bg-white border border-gray-200 rounded text-gray-500 text-xs font-medium">↵</kbd>
            <span>selecionar</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <kbd className="inline-flex items-center px-1.5 py-0.5 bg-white border border-gray-200 rounded text-gray-500 text-xs font-medium">ESC</kbd>
            <span>fechar</span>
          </div>
        </div>
      </div>
    </div>
  );
}
